import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { TrendingUp } from 'lucide-react'
import { AlertBanner } from '../components/ui/AlertBanner'
import { KPICard } from '../components/ui/KPICard'
import { PageHeader } from '../components/ui/PageHeader'
import { YearSelector } from '../components/ui/YearSelector'
import { RevenueOpsChart } from '../components/dashboard/RevenueOpsChart'
import { BacklogAgingBar } from '../components/dashboard/BacklogAgingBar'
import { useRevenueOpsKPIs, useAvailableYears } from '../hooks/useKPIs'
import { useGlobalFilters } from '../contexts/GlobalFiltersContext'
import { useBUContext } from '../contexts/BUContext'

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function PanelHeader({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{label}</p>
      </div>
      {children}
    </div>
  )
}

interface PipelineTableProps {
  confirmed: number
  probable: number
  recognitionRate: number
  gap: number
  winRate: number | null
  avgCompletion: number
}

function PipelineTable({
  confirmed,
  probable,
  recognitionRate,
  gap,
  winRate,
  avgCompletion,
}: PipelineTableProps) {
  const rows = [
    {
      label: 'Ordinato confermato',
      value: formatEur(confirmed),
      note: 'Status confermato o in lavorazione',
      highlight: false,
    },
    {
      label: 'Pipeline probabile',
      value: formatEur(probable),
      note: 'Offerte in corso ad alta probabilità',
      highlight: false,
    },
    {
      label: 'Recognition rate',
      value: formatPct(recognitionRate),
      note: 'Competenziato / Ordinato %',
      highlight: recognitionRate < 70,
    },
    {
      label: 'Gap Ordinato → Competenziato',
      value: formatEur(gap),
      note: 'Da riconoscere nei prossimi mesi',
      highlight: gap > confirmed * 0.3,
    },
    {
      label: 'Win rate',
      value: winRate !== null ? formatPct(winRate) : 'N/D',
      note: winRate !== null ? 'Offerte vinte / totale offerte' : 'Dati insufficienti',
      highlight: winRate !== null && winRate < 30,
    },
    {
      label: 'Completamento medio commesse',
      value: formatPct(avgCompletion),
      note: 'Media % avanzamento commesse attive',
      highlight: false,
    },
  ]

  return (
    <div
      className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
      data-testid="pipeline-table"
    >
      <div className="px-6 py-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-2">
          <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
          <p className="section-label">Pipeline — dettaglio</p>
        </div>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {rows.map(row => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 hover:bg-white/[0.02] transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[--km-text-primary] leading-tight">{row.label}</p>
              <p className="text-xs text-[--km-text-muted] mt-0.5">{row.note}</p>
            </div>
            <span className={`shrink-0 text-sm font-semibold tabular-nums ${
              row.highlight ? 'text-amber-400' : 'text-[--km-text-primary]'
            }`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function RevenueOps() {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number | 'cumulative'>(currentYear)
  const { data: availableYears = [currentYear] } = useAvailableYears()
  const { filters, apiDateFrom, apiDateTo } = useGlobalFilters()
  const { selectedBuId } = useBUContext()
  const { data: kpis, isLoading, error } = useRevenueOpsKPIs({
    year: selectedYear,
    apiDateFrom,
    apiDateTo,
    customer: filters.customer,
    service_line: filters.service_line,
    buId: selectedBuId,
  })

  const pipelineCoverageDelta =
    kpis && kpis.pipeline_coverage_pct >= 100
      ? `+${(kpis.pipeline_coverage_pct - 100).toFixed(1)}% vs target`
      : kpis
        ? `${(kpis.pipeline_coverage_pct - 100).toFixed(1)}% vs target`
        : undefined

  const subtitle = kpis
    ? `Aggiornato al ${format(new Date(kpis.computed_at), "d MMM yyyy 'ore' HH:mm", { locale: it })}`
    : format(new Date(), "d MMM yyyy", { locale: it })

  return (
    <section aria-label="Revenue Operations" className="space-y-6">
      <PageHeader
        title="Revenue Operations"
        subtitle={subtitle}
        icon={TrendingUp}
        iconColor="from-emerald-500 to-teal-500"
        coveragePct={kpis?.coverage_score_pct}
        loading={isLoading}
        actions={
          <YearSelector
            years={availableYears}
            value={selectedYear}
            onChange={setSelectedYear}
            loading={isLoading}
            disabled={!!(apiDateFrom && apiDateTo)}
          />
        }
      />

      {error && (
        <AlertBanner
          severity="critical"
          message="Impossibile caricare i KPI Revenue Operations. Riprovare."
        />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="stagger-1">
          <KPICard
            label="Ordinato confermato"
            value={kpis ? formatEur(kpis.confirmed_orders) : '—'}
            status="neutral"
            loading={isLoading}
            subtext="Ordini in lavorazione"
          />
        </div>
        <div className="stagger-2">
          <KPICard
            label="Pipeline totale"
            value={kpis ? formatEur(kpis.confirmed_orders + kpis.probable_orders) : '—'}
            delta={pipelineCoverageDelta}
            deltaPositive={kpis ? kpis.pipeline_coverage_pct >= 100 : undefined}
            status={kpis
              ? kpis.pipeline_coverage_pct >= 100 ? 'positive'
                : kpis.pipeline_coverage_pct >= 80 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext={kpis ? `${formatPct(kpis.target_coverage_pct)} del target annuale` : undefined}
          />
        </div>
        <div className="stagger-3">
          <KPICard
            label="Recognition rate"
            value={kpis ? formatPct(kpis.recognition_rate_pct) : '—'}
            status={kpis
              ? kpis.recognition_rate_pct >= 80 ? 'positive'
                : kpis.recognition_rate_pct >= 60 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Competenziato / Ordinato"
          />
        </div>
        <div className="stagger-4">
          <KPICard
            label="Win rate"
            value={kpis ? (kpis.win_rate_pct !== null ? formatPct(kpis.win_rate_pct) : 'N/D') : '—'}
            status={kpis && kpis.win_rate_pct !== null
              ? kpis.win_rate_pct >= 40 ? 'positive'
                : kpis.win_rate_pct >= 25 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Offerte vinte / totale"
          />
        </div>
      </div>

      {/* Trend + Aging */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Trend mensile — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Trend mensile — ultimi 12 mesi" />
          <div className="h-60">
            <RevenueOpsChart data={kpis?.monthly_trend ?? []} />
          </div>
        </div>

        {/* Backlog aging — 1/3 */}
        <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Backlog aging" />

          {isLoading ? (
            <div className="space-y-5 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2">
                  <div className="h-2.5 w-32 rounded-full bg-[--km-surface-3]" />
                  <div className="h-1.5 w-full rounded-full bg-[--km-surface-3]" />
                </div>
              ))}
            </div>
          ) : kpis ? (
            <BacklogAgingBar aging={kpis.backlog_aging} />
          ) : null}

          {kpis && (
            <div className="mt-5 pt-4 border-t border-white/[0.05]">
              <div className="flex justify-between items-center text-xs">
                <span className="text-[--km-text-muted]">Completamento medio</span>
                <span className={`font-semibold tabular-nums ${
                  kpis.avg_completion_pct >= 70 ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {formatPct(kpis.avg_completion_pct)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pipeline table */}
      {!isLoading && kpis && (
        <PipelineTable
          confirmed={kpis.confirmed_orders}
          probable={kpis.probable_orders}
          recognitionRate={kpis.recognition_rate_pct}
          gap={kpis.gap_orders_recognized}
          winRate={kpis.win_rate_pct}
          avgCompletion={kpis.avg_completion_pct}
        />
      )}

      {kpis && kpis.coverage_score_pct < 80 && (
        <AlertBanner
          severity="warning"
          message={`Copertura dati: ${(kpis.coverage_score_pct ?? 0).toFixed(0)}% dei KPI calcolabili. Caricare più dati per ottenere un quadro completo.`}
        />
      )}
    </section>
  )
}
